import type { ReactElement, ReactNode } from 'react';

/**
 * #644: AI replies in Main Command are written in a little Markdown - **bold**, `code`, short
 * bullet or numbered lists, paragraphs. Shown raw, "**UX findings (advisory):**" reads as
 * noise. This renders exactly that subset as plain React elements: no HTML is parsed, no
 * link or image is followed, and anything else stays the literal text it was.
 */

type Block =
  | { readonly kind: 'p'; readonly lines: readonly string[] }
  | { readonly kind: 'ul' | 'ol'; readonly items: readonly string[] };

const BULLET = /^\s*(?:[-*•])\s+(.*)$/;
const NUMBERED = /^\s*\d{1,3}[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,4}\s+(.*)$/;

function blocks(text: string): Block[] {
  const out: Block[] = [];
  let paragraph: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;
  const flush = (): void => {
    if (paragraph.length > 0) out.push({ kind: 'p', lines: paragraph });
    if (list !== null) out.push(list);
    paragraph = [];
    list = null;
  };
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (raw.trim() === '') { flush(); continue; }
    const bullet = BULLET.exec(raw);
    const numbered = bullet === null ? NUMBERED.exec(raw) : null;
    const item = bullet ?? numbered;
    if (item !== null) {
      const kind = bullet !== null ? 'ul' : 'ol';
      if (paragraph.length > 0) { out.push({ kind: 'p', lines: paragraph }); paragraph = []; }
      if (list === null || list.kind !== kind) {
        if (list !== null) out.push(list);
        list = { kind, items: [] };
      }
      list.items.push(item[1] ?? '');
      continue;
    }
    if (list !== null && /^\s{2,}\S/.test(raw)) {
      // An indented line continues the list item above it.
      list.items[list.items.length - 1] = `${list.items[list.items.length - 1]} ${raw.trim()}`;
      continue;
    }
    if (list !== null) { out.push(list); list = null; }
    const heading = HEADING.exec(raw);
    paragraph.push(heading !== null ? `**${(heading[1] ?? '').replace(/\*\*/g, '')}**` : raw);
  }
  flush();
  return out;
}

/** `code` and **bold**, nothing else; an unmatched marker stays literal. */
function inline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const token = /`([^`\n]+)`|\*\*([^*\n][^\n]*?)\*\*/g;
  let last = 0;
  let index = 0;
  for (const match of text.matchAll(token)) {
    const at = match.index ?? 0;
    if (at > last) nodes.push(text.slice(last, at));
    if (match[1] !== undefined) nodes.push(<code key={`${keyBase}:c${index}`}>{match[1]}</code>);
    else nodes.push(<strong key={`${keyBase}:b${index}`}>{match[2]}</strong>);
    last = at + match[0].length;
    index += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function ChatText({ text }: { readonly text: string }): ReactElement {
  const parsed = blocks(text);
  return (
    <>
      {parsed.map((block, blockIndex) => {
        const key = `b${blockIndex}`;
        if (block.kind === 'p') {
          return (
            <p key={key}>
              {block.lines.map((line, lineIndex) => (
                <span key={`${key}:${lineIndex}`}>
                  {lineIndex > 0 && <br />}
                  {inline(line, `${key}:${lineIndex}`)}
                </span>
              ))}
            </p>
          );
        }
        const items = block.items.map((item, itemIndex) => (
          <li key={`${key}:${itemIndex}`}>{inline(item, `${key}:${itemIndex}`)}</li>
        ));
        return block.kind === 'ul' ? <ul key={key}>{items}</ul> : <ol key={key}>{items}</ol>;
      })}
    </>
  );
}
